import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
  viewChild,
  computed,
  input,
  TemplateRef
} from '@angular/core';
import { Router } from '@angular/router';
import {
  Subject,
  take,
  takeUntil
} from 'rxjs';

import {
  InsuranceReviewComponent,
  InsuranceVerificationFailedComponent
} from '@sondermind/features/account-edit';
import { AccountSetupContext, BasicInfoStartedType } from '@sondermind/constants';

import { ProposeATimeStore } from '@sondermind/data-access/client-facing/propose-a-time';
import {
  CurrentUser,
  ClientUserTags
} from '@sondermind/data-access/client-user';
import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { PatientInsuranceStore } from '@sondermind/data-access/client-insurance';

import { SegmentService } from '@sondermind/segment';
import { ClientUser } from '@sondermind/utilities/models-user-client';
import { AccountSetupService } from '@sondermind/utilities/services';
import { ICostEstimateContextAttributes } from '@sondermind/utilities/models-client-insurance';
import { Logger } from '@sondermind/datadog-logging';

import { ProviderStore } from '@sondermind/data-access/client-portal-referrals';
import {
  ClientCostEstimationComponent,
  SelfPayCostEstimationComponent
} from '@sondermind/features/client-facing/cost-estimation';
import {
  IInsuranceInfoSubmitted,
  SetupInsuranceInfoComponent
} from './components/setup-insurance-info/setup-insurance-info.component';
import { SetupBasicInfoComponent } from './components/setup-basic-info/setup-basic-info.component';
import { SetupEAPInfoComponent } from './components/setup-eap-info/setup-eap-info.component';
import { SetupPaymentInfoComponent } from './components/setup-payment-info/setup-payment-info.component';
import { SetupEbcgPaymentInfoComponent } from './components/setup-ebcg-payment-info/setup-ebcg-payment-info.component';

@Component({
  selector: 'client-account-setup',
  templateUrl: './account-setup.component.html',
  styleUrls: ['./account-setup.component.scss'],
  standalone: false
})
export class AccountSetupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user!: ClientUser;
  @Input() isMobile: boolean = false;
  @Input() isProposeATime: boolean = false;
  @Input() isRequestToSchedule: boolean = false;
  @Input() useInSessionRequest: boolean = false;
  @Input() processing: boolean = false;
  @Input() costEstimateContext!: ICostEstimateContextAttributes;
  readonly isInsuranceEmpty = input.required<boolean>();
  readonly hasSelfPayInsuranceRecord = input<boolean>(false);
  readonly fromDirectBooking = input<boolean>(false);
  readonly ebcgEmbraceProviderFromDirectBooking = input<boolean>(false);
  readonly providerNameFromDirectBooking = input<string>('');
  /** From parent (e.g. AccountSetupPage) when account setup runs outside direct booking. */
  readonly ebcgEmbraceProviderFromUpcomingAppointment = input<boolean>(false);
  readonly providerNameFromUpcomingAppointment = input<string>('');

  /** Only used when fromDirectBooking is true: pending appointment info for self-pay cost estimate */
  readonly providerPersonaProviderId = input<number>();
  readonly providerIsPsychiatrist = input<boolean>();
  readonly isTelehealthAppointment = input<boolean>();

  @Output() progressBarValueUpdate: EventEmitter<number> = new EventEmitter<number>();
  @Output() setupDataSubmit: EventEmitter<void> = new EventEmitter<void>();
  @Output() setupExited: EventEmitter<void> = new EventEmitter<void>();
  @Output() setupConfirmed: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('scrollAnchor')
    scrollAnchor!: ElementRef<HTMLElement>;

  // Template reference for the current action button that parent can render
  readonly accountSetupActionButton =
    viewChild.required<TemplateRef<unknown>>('accountSetupActionButton');

  // ViewChild references for each setup step component
  readonly basicInfoComponent = viewChild<SetupBasicInfoComponent>('basicInfoComponent');
  readonly eapInfoComponent = viewChild<SetupEAPInfoComponent>('eapInfoComponent');
  readonly insuranceInfoComponent = viewChild<SetupInsuranceInfoComponent>('insuranceInfoComponent');
  readonly paymentInfoComponent = viewChild<SetupPaymentInfoComponent>('paymentInfoComponent');
  readonly ebcgPaymentInfoComponent = viewChild<SetupEbcgPaymentInfoComponent>('ebcgPaymentInfoComponent');
  readonly insuranceReviewComponent = viewChild<InsuranceReviewComponent>('insuranceReviewComponent');
  readonly insuranceVerificationFailedComponent =
    viewChild<InsuranceVerificationFailedComponent>('insuranceVerificationFailedComponent');
  readonly costEstimationComponent = viewChild<ClientCostEstimationComponent>('costEstimationComponent');
  readonly selfPayCostEstimationComponent =
    viewChild<SelfPayCostEstimationComponent>('selfPayCostEstimationComponent');

  /**
   * Computed signal that returns the current step's button template.
   * Used by parent component to render the appropriate action button.
   */
  readonly currentActionButtonTemplate = computed<TemplateRef<unknown> | null | undefined>(() => {
    if (this.onBasicInfo()) {
      const component = this.basicInfoComponent();
      return component ? component.actionButtonTemplate() : null;
    }
    if (this.onEAP()) {
      const component = this.eapInfoComponent();
      return component ? component.actionButtonTemplate() : null;
    }
    if (this.onInsurance()) {
      const component = this.insuranceInfoComponent();
      return component ? component.actionButtonTemplate() : null;
    }
    if (this.onPayment()) {
      const component = this.paymentInfoComponent();
      return component ? component.actionButtonTemplate() : null;
    }
    if (this.onEbcgPayment()) {
      const component = this.ebcgPaymentInfoComponent();
      // Hide action button while waiting for payment or when error occurred
      if (component?.waitingForPayment() || component?.paymentFailed()) {
        return null;
      }
      return component ? component.actionButtonTemplate() : null;
    }
    if (this.onInsuranceReview()) {
      const component = this.insuranceReviewComponent();
      return component ? component.actionButtonTemplate() : null;
    }
    if (this.onInsuranceVerificationFailed()) {
      const component = this.insuranceVerificationFailedComponent();
      return component ? component.actionButtonTemplate() : null;
    }
    if (this.onCostEstimation()) {
      if (this.isSelfPayCostEstimate()) {
        const component = this.selfPayCostEstimationComponent();
        return component ? component.actionButtonTemplate() : null;
      }
      const component = this.costEstimationComponent();
      return component ? component.actionButtonTemplate() : null;
    }
    return null;
  });

  private destroyed$ = new Subject<void>();

  progressBarHeaderBasicInfo: string = 'Fill in a few details to get in touch';
  progressBarHeaderInsurance: string = 'Would you like to use any insurance benefits?';
  progressBarHeaderEAP: string = 'Would you like to use any Employee Assistance Program benefits?';
  progressBarHeaderPayment: string = 'Add your payment method';

  // used for showing the correct setup step - using signals for reactivity
  readonly onBasicInfo = signal<boolean>(false);
  readonly onInsurance = signal<boolean>(false);
  readonly onInsuranceReview = signal<boolean>(false);
  readonly onInsuranceVerificationFailed = signal<boolean>(false);
  readonly isEAPReview = signal<boolean>(false);
  readonly onEAP = signal<boolean>(false);
  readonly onPayment = signal<boolean>(false);
  readonly onEbcgPayment = signal<boolean>(false);
  readonly onCostEstimation = signal<boolean>(false);

  showPaymentFaq: boolean = false;
  basicInfoStartedType: BasicInfoStartedType = BasicInfoStartedType.INDEPENDENT;
  accountSetupType: AccountSetupContext = AccountSetupContext.INDEPENDENT;

  progressBarValue: number = 0;
  progressBarHeader: string = '';

  userHasSuccessfulMostRecentBv: boolean = false;

  private attestationsJustSucceeded: boolean = false;
  private insuranceJustSucceeded: boolean = false;
  private insuranceJustRejected: boolean = false;
  private readonly selfPayJustSelected = signal<boolean>(false);
  private payorDowntimeJustAccepted: boolean = false;
  private readonly costEstimationReviewed = signal<boolean>(false);

  readonly insuranceSetupHadBvError = signal<boolean>(false);
  private readonly isSaasInvited = signal<boolean>(false);
  readonly loadingFeatureFlag = signal<boolean>(true);
  private readonly validForEbcgPaymentCollection = signal<boolean>(false);
  private readonly loadingInsuranceEligibility = signal<boolean>(false);
  private readonly ebcgEligibilityCheckStarted = signal<boolean>(false);

  /**
   * Computed signal to check if we're still loading critical data for payment page determination.
   */
  readonly isLoadingPaymentPageData = computed(() => {
    if (this.loadingFeatureFlag()) {
      return true;
    }

    // Check if EBCG eligibility data is still loading
    if (!this.ebcgEligibilityCheckStarted()) {
      return true;
    }
    if (this.loadingInsuranceEligibility()) {
      return true;
    }

    return false;
  });

  /**
   * Effective EBCG embrace provider: from input when in direct booking,
   * from upcoming appointment's provider when outside direct booking.
   */
  readonly ebcgEmbraceProvider = computed(() => (
    this.fromDirectBooking()
      ? this.ebcgEmbraceProviderFromDirectBooking()
      : this.ebcgEmbraceProviderFromUpcomingAppointment()
  ));

  /**
   * Effective provider name for EBCG payment screen: from input when in direct booking,
   * from upcoming appointment's provider when outside direct booking.
   */
  readonly providerName = computed(() => (
    this.fromDirectBooking()
      ? this.providerNameFromDirectBooking()
      : this.providerNameFromUpcomingAppointment()
  ));

  /**
   * True when the cost estimation step is shown for a self-pay client (no insurance)
   */
  readonly isSelfPayCostEstimate = computed(() => this.onCostEstimation() &&
    !this.insuranceSetupHadBvError() &&
    (this.selfPayJustSelected() || this.isInsuranceEmpty() === true));

  constructor(
    private router: Router,
    private currentUser: CurrentUser,
    private proposeATimeStore: ProposeATimeStore,
    private accountSetupService: AccountSetupService,
    private segmentService: SegmentService<SondermindEvents>,
    // NOTE: FOR PROJECT JUNO
    private providerStore: ProviderStore,
    private patientInsuranceStore: PatientInsuranceStore,
    private logger: Logger
  ) { }

  ngOnInit(): void {
    // Wait for SAAS check to emit first value
    this.providerStore.isClientSaasInvited$().pipe(
      take(1),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: (saasInvited) => {
        // Set SAAS status
        this.isSaasInvited.set(saasInvited);

        // Check insurance eligibility for EBCG
        this.checkInsuranceEligibilityFromStore();

        this.loadingFeatureFlag.set(false);

        // Now that flags are loaded, check account status
        this.checkAccountStatus();

        this.accountSetupService.clearContext();
        this.setAccountSetupContext();
        this.sendToSegment();
        this.checkUserBenefitVerification();
      }
    });
  }

  ngOnDestroy(): void {
    this.accountSetupService.clearContext();

    this.insuranceJustRejected = false;

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // when user input changes, determine account status
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user'] || changes['isInsuranceEmpty']) {
      // Only check account status if all required data is loaded
      // Otherwise, checkAccountStatus will be called when loading completes
      if (!this.isLoadingPaymentPageData()) {
        this.checkAccountStatus();
      }
      this.checkShowPaymentFaq();
      this.checkUserBenefitVerification();
    }
  }

  setAccountSetupContext(): void {
    if (this.isProposeATime) {
      this.accountSetupService.context = AccountSetupContext.PROPOSE_A_TIME;
      this.basicInfoStartedType = BasicInfoStartedType.PROPOSE_A_TIME;
      this.accountSetupType = AccountSetupContext.PROPOSE_A_TIME;
    } else if (this.useInSessionRequest) {
      this.accountSetupService.context = AccountSetupContext.REQUEST_TO_SCHEDULE;
      this.basicInfoStartedType = BasicInfoStartedType.REQUEST_TO_SCHEDULE;
      this.accountSetupType = AccountSetupContext.REQUEST_TO_SCHEDULE;
    } else {
      this.accountSetupService.context = AccountSetupContext.INDEPENDENT;
      this.basicInfoStartedType = BasicInfoStartedType.INDEPENDENT;
      this.accountSetupType = AccountSetupContext.INDEPENDENT;
    }
  }

  sendToSegment(): void {
    if (!this.onBasicInfo()) return;

    this.segmentService.track(
      SondermindEvent.BasicInfoStarted,
      {
        acType: this.basicInfoStartedType,
        directBookingModal: this.fromDirectBooking()
      }
    ).catch(() => {});
  }

  /**
   * On submit of a setup step, tell parent.
   * reviewInsurance only comes on insurance submit, and will be true if BV fails
   */
  setupDataSubmitted(insuranceInfoSubmitted: IInsuranceInfoSubmitted = {
    reviewInsurance: false,
    verificationOutage: false
  }): void {
    if (insuranceInfoSubmitted.exitSetup) {
      this.insuranceJustRejected = true;
      this.setupExited.emit();
      return;
    }

    if (insuranceInfoSubmitted.bvErrorEncountered === true) {
      this.insuranceSetupHadBvError.set(true);
    }

    // If we're coming from basic info step, mark that attestations were just submitted successfully
    // This handles the race condition where backend attestation flags aren't updated immediately
    // Note: This event only fires when attestations actually succeed (not on failure)
    if (this.onBasicInfo()) {
      this.attestationsJustSucceeded = true;
    }

    // If we're coming from insurance step and no BV error, mark that insurance was just submitted successfully
    if (this.onInsurance() && !insuranceInfoSubmitted.reviewInsurance) {
      this.insuranceJustSucceeded = true;
      // Refresh insurance list to get updated validForEbcgPaymentCollection values
      // This ensures the correct payment page is shown after insurance is added
      this.checkInsuranceEligibilityFromStore();
    }

    // If self-pay was just selected, mark that to prevent showing insurance step again
    // This handles the race condition where backend user flags aren't updated immediately after self-pay
    if (insuranceInfoSubmitted.selfPaySelected) {
      this.selfPayJustSelected.set(true);
    }

    if (insuranceInfoSubmitted.payorDowntimeAccepted) {
      this.payorDowntimeJustAccepted = true;
    }

    if (insuranceInfoSubmitted.reviewInsurance) {
      this.onBasicInfo.set(false);
      this.onInsurance.set(false);
      this.onEAP.set(false);
      this.onPayment.set(false);

      if (!insuranceInfoSubmitted.verificationOutage) {
        this.onInsuranceReview.set(true);
        this.onInsuranceVerificationFailed.set(false);
      } else {
        this.onInsuranceReview.set(false);
        this.onInsuranceVerificationFailed.set(true);
      }

      this.scrollToTop();
    } else {
      // If not showing review screen, emit to trigger next step
      this.setupDataSubmit.emit();
    }
  }

  checkAccountStatus(): void {
    // Wait for all critical data to load before determining payment page
    if (this.isLoadingPaymentPageData()) {
      return;
    }

    this.onBasicInfo.set(false);
    this.onInsurance.set(false);
    this.onEAP.set(false);
    this.onPayment.set(false);
    this.onEbcgPayment.set(false);
    this.onInsuranceReview.set(false);
    this.onInsuranceVerificationFailed.set(false);
    this.onCostEstimation.set(false);

    this.attestationsJustSucceeded = false;
    this.insuranceJustRejected = false;
    // Don't reset insuranceJustSucceeded yet - we need it to determine if payment is required
    // Don't reset selfPayJustSelected yet - we need it for step determination
    // Don't reset payorDowntimeJustAccepted yet - we need it for step determination

    if (this.accountComplete() && this.isProposeATime) {
      this.proposeATimeStore.updateInPATAccountSetupComplete(true);
      this.proposeATimeStore.setHideBackButton(false);
    }

    if (this.accountComplete() && !this.useInSessionRequest && !this.isProposeATime) {
      if (this.fromDirectBooking()) {
        this.setupConfirmed.emit();
      } else {
        this.router.navigate(['/profile/setup-complete']);
      }
    } else {
      if (this.missingBasicInfo()) {
        this.onBasicInfo.set(true);
        this.progressBarHeader = this.progressBarHeaderBasicInfo;
        // Reset flags when moving to a different step
        this.insuranceJustSucceeded = false;
        this.selfPayJustSelected.set(false);
        this.payorDowntimeJustAccepted = false;
        this.costEstimationReviewed.set(false);
      } else if (this.missingEAPInfo()) {
        this.onEAP.set(true);
        this.progressBarHeader = this.progressBarHeaderEAP;
        // Reset flags when moving to a different step
        this.insuranceJustSucceeded = false;
        this.selfPayJustSelected.set(false);
        this.payorDowntimeJustAccepted = false;
        this.costEstimationReviewed.set(false);
      } else if (this.missingInsuranceInfo()) {
        this.onInsurance.set(true);
        this.progressBarHeader = this.progressBarHeaderInsurance;
        // Reset flags when moving to a different step
        this.insuranceJustSucceeded = false;
        this.selfPayJustSelected.set(false);
        this.payorDowntimeJustAccepted = false;
        this.costEstimationReviewed.set(false);
      } else if (
        !this.isProposeATime &&
        !this.isRequestToSchedule &&
        !this.useInSessionRequest &&
        !this.isSaasInvited() &&
        !this.costEstimationReviewed()
      ) {
        this.onCostEstimation.set(true);
        // Do not reset selfPayJustSelected here; we need it for isSelfPayCostEstimate()
        // Do not reset insuranceJustSucceeded here; cost estimation may follow insurance submission
        this.payorDowntimeJustAccepted = false;
      } else if (this.missingFinancialInfo()) {
        // Determine which payment screen to show
        if (this.shouldUseEbcgPayment()) {
          this.onEbcgPayment.set(true);
        } else {
          this.onPayment.set(true);
        }
        this.progressBarHeader = this.progressBarHeaderPayment;
        // Keep selfPayJustSelected true while on payment step
        // Keep payorDowntimeJustAccepted true while on payment step
        // Keep insuranceJustSucceeded true while on payment step (but should be false due to check in missingFinancialInfo)
      } else {
        // All steps complete - now we can reset all flags
        this.insuranceJustSucceeded = false;
        this.selfPayJustSelected.set(false);
        this.payorDowntimeJustAccepted = false;
        this.costEstimationReviewed.set(false);
      }

      // Safety check: if no step is showing, log error to diagnose logic bugs
      const hasActiveStep = this.onBasicInfo() || this.onEAP() || this.onInsurance() ||
        this.onInsuranceReview() || this.onInsuranceVerificationFailed() ||
        this.onCostEstimation() || this.onPayment() || this.onEbcgPayment();

      if (!hasActiveStep && !this.accountComplete()) {
        this.logger.error('Account setup rendering blank page: no active step but account incomplete', {
          user: {
            personaId: this.user?.personaId,
            needsSetup: this.user?.needsSetup,
            requiresProfileVerification: this.user?.requiresProfileVerification,
            requiresInsuranceInfo: this.user?.requiresInsuranceInfo,
            requiresCreditCard: this.user?.requiresCreditCard,
            requiresFinancialAgreement: this.user?.requiresFinancialAgreement
          },
          flags: {
            attestationsJustSucceeded: this.attestationsJustSucceeded,
            insuranceJustSucceeded: this.insuranceJustSucceeded,
            insuranceJustRejected: this.insuranceJustRejected,
            selfPayJustSelected: this.selfPayJustSelected(),
            payorDowntimeJustAccepted: this.payorDowntimeJustAccepted,
            costEstimationReviewed: this.costEstimationReviewed()
          },
          stepChecks: {
            missingBasicInfo: this.missingBasicInfo(),
            missingEAPInfo: this.missingEAPInfo(),
            missingInsuranceInfo: this.missingInsuranceInfo(),
            missingFinancialInfo: this.missingFinancialInfo()
          }
        });
        // Fallback: show basic info to prevent blank page
        this.onBasicInfo.set(true);
      }

      // flag visit to route, duplicate calls are handled by back end so that only first visit is recorded
      this.currentUser.setHasVisitedAccountSetup(this.user.personaId);

      this.updateProgressBarValue();
      this.scrollToTop();
    }
  }

  /**
   * Calculate progress bar value for setup progress.
   * NOTE: does not mathematically account well for when a user has completed some steps, leaves, and returns
   * OR when user does not initially require certain information to be complete such as VA clients with payment info
   */
  updateProgressBarValue(): void {
    let progressBarValue: number;
    let progressBarIncrement: number;

    if (this.isSaasInvited()) {
      // For SAAS clients: only 2 steps (basic info + payment)
      progressBarValue = 25;
      progressBarIncrement = 50;

      if (!this.missingBasicInfo()) {
        progressBarValue += progressBarIncrement;
        this.proposeATimeStore.setBasicInfoSubmitted(true);
      }

      if (!this.missingFinancialInfo()) {
        progressBarValue += progressBarIncrement;
      }
    } else {
      // Original logic for non-SAAS clients
      progressBarValue = this.useInSessionRequest ? 40 : 25;
      progressBarIncrement = this.useInSessionRequest ? 20 : 25;

      if (!this.missingBasicInfo()) {
        progressBarValue += progressBarIncrement;
        this.proposeATimeStore.setBasicInfoSubmitted(true);
      }

      if (!(this.missingInsuranceInfo() || this.missingEAPInfo())) {
        progressBarValue += progressBarIncrement;
      }

      if (!this.missingFinancialInfo()) {
        progressBarValue += progressBarIncrement;
      }
    }

    if (this.useInSessionRequest || this.fromDirectBooking()) {
      this.progressBarValueUpdate.emit(progressBarValue);
    } else {
      this.progressBarValue = progressBarValue;
    }
  }

  /**
   * Used ONLY in case the client selected EAP and Insurance in match flow
   * but declined to fill out the EAP form during setup.
   */
  eapToInsurance(): void {
    this.onBasicInfo.set(false);
    this.onEAP.set(false);
    this.onPayment.set(false);
    this.onInsuranceReview.set(false);
    this.onInsuranceVerificationFailed.set(false);
    this.onInsurance.set(true);
    this.progressBarHeader = this.progressBarHeaderInsurance;
  }

  /**
   * After each form is completed, return to top of screen.
   */
  scrollToTop(): void {
    setTimeout(() => {
      this.scrollAnchor?.nativeElement?.scrollIntoView();
    }, 0);
  }

  /**
   * Do not show payment FAQ data for psychiatry or va clients because it is inaccurate for those clients.
   * Check for experiment text to show for PE-17684 ABC testing.
   */
  checkShowPaymentFaq(): void {
    this.showPaymentFaq = !(this.user?.tags?.includes(ClientUserTags.VA_CLIENT)
      || this.user?.tags?.includes(ClientUserTags.PSYCHIATRY_CLIENT));
  }

  onCostEstimationReviewed(): void {
    this.costEstimationReviewed.set(true);
    this.onCostEstimation.set(false);
    if (this.missingFinancialInfo()) {
      if (this.shouldUseEbcgPayment()) {
        this.onEbcgPayment.set(true);
      } else {
        this.onPayment.set(true);
      }
    } else {
      // No payment step is required after reviewing the cost estimate. Emit so the parent reloads
      // the user and re-evaluates account status, advancing the flow instead of leaving a blank,
      // step-less page behind a stale progress-bar header.
      this.setupDataSubmit.emit();
    }
    this.scrollToTop();
  }

  /**
   * Check if client's insurance is valid for EBCG payment collection using PatientInsuranceStore
   */
  checkInsuranceEligibilityFromStore(): void {
    this.ebcgEligibilityCheckStarted.set(true);
    this.loadingInsuranceEligibility.set(true);

    this.patientInsuranceStore.loadPatientInsuranceList$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          // Check ALL insurances in the store for validForEbcgPaymentCollection
          this.patientInsuranceStore.fullInsuranceList$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((insurances) => {
              const hasEbcgInsurance = (insurances ?? []).some(
                (insurance) => insurance.providerNetworkCompany?.validForEbcgPaymentCollection === true
              );

              this.validForEbcgPaymentCollection.set(hasEbcgInsurance);
              this.loadingInsuranceEligibility.set(false);

              // Re-check account status now that eligibility is loaded
              this.checkAccountStatus();
            });
        },
        error: () => {
          // If there's an error, assume not valid and fall back to regular payment
          this.validForEbcgPaymentCollection.set(false);
          this.loadingInsuranceEligibility.set(false);

          // Re-check account status even on error
          this.checkAccountStatus();
        }
      });
  }

  /**
   * Determine if the user should see the EBCG payment screen instead of the regular payment screen.
   * Requires provider to be an EBCG Embrace provider,
   * client to have valid insurance for EBCG payment collection, and client to be EBCG-eligible by creation date.
   */
  readonly shouldUseEbcgPayment = computed(() => this.ebcgEmbraceProvider() === true &&
    this.validForEbcgPaymentCollection() &&
    this.user?.clientEbcgEligibleByCreationDate === true);

  missingBasicInfo(): boolean {
    // If attestations just succeeded, don't rely on backend flags immediately
    // The backend takes time to update attestation flags, but we know the submission was successful
    // This flag is only set when attestations actually succeed (not on failure)
    if (this.attestationsJustSucceeded) {
      return false;
    }

    const { firstName, lastName, birthDate, phoneNumber, mailingAddress, genderId } = this.user?.profile;
    const isProfileInfoComplete =
      !!firstName &&
      !!lastName &&
      !!birthDate &&
      !!phoneNumber &&
      !!genderId &&
      !!mailingAddress?.addressLine1 &&
      !!mailingAddress?.city &&
      !!mailingAddress?.state &&
      !!mailingAddress?.postalCode;

    return this.user?.requiresProfileVerification
      || this.user?.requiresHipaaAgreement
      || this.user?.requiresTosAgreement
      || !isProfileInfoComplete;
  }

  /**
   * Returns true when the insurance step needs to be shown.
   * Guards against race conditions where backend flags haven't updated yet after a user action.
   */
  private missingInsuranceInfo(): boolean {
    if (this.isSaasInvited() ||
    this.insuranceJustSucceeded ||
    this.insuranceJustRejected ||
    this.selfPayJustSelected() ||
    this.payorDowntimeJustAccepted ||
    this.hasSelfPayInsuranceRecord()) {
      return false;
    }

    return this.user?.requiresInsuranceInfo || this.isInsuranceEmpty();
  }

  private missingEAPInfo(): boolean {
    // Skip EAP step for SAAS-invited clients
    if (this.isSaasInvited()) {
      return false;
    }

    return this.user?.hasEapPref && this.user?.requiresInsuranceInfo;
  }

  private missingFinancialInfo(): boolean {
    // If self-pay was just selected, user needs to add payment method
    if (this.selfPayJustSelected()) {
      return true;
    }

    // If payor downtime was just accepted, user needs to add payment method
    if (this.payorDowntimeJustAccepted) {
      return true;
    }

    // If insurance was just successfully submitted, wait for backend to update requiresCreditCard flag
    // This prevents showing payment screen for insurance that doesn't require payment
    // Once backend processes the insurance, requiresCreditCard will be updated accordingly.
    // Only suppress payment up to and including the cost-estimation step; once the client has
    // reviewed the cost estimate, requiresCreditCard is authoritative and must drive the payment
    // step, otherwise a client who genuinely needs payment gets stranded on a blank page.
    if (this.insuranceJustSucceeded && !this.costEstimationReviewed()) {
      return false;
    }

    return this.user?.requiresCreditCard
      || this.user?.requiresFinancialAgreement;
  }

  private accountComplete(): boolean {
    return this.user != null && !this.user.needsSetup;
  }

  private checkUserBenefitVerification(): void {
    this.userHasSuccessfulMostRecentBv = !!this.user?.hasSuccessfulMostRecentBv;
  }

  onInsuranceInfoSubmitted(event: IInsuranceInfoSubmitted): void {
    this.setupDataSubmitted(event);
  }
}
